/**
 * Agent playbooks & per-agent default policy — landlord API client.
 *
 * Covers:
 *   GET   /landlord/api/agent-playbooks
 *   POST  /landlord/api/agent-playbooks/:agentKind
 *   GET   /landlord/api/agent-policy/default/:agentKind
 *   PATCH /landlord/api/agent-policy/default/:agentKind
 *
 * Types are derived from the boundary contract in `@resida/api-types/agent`.
 * The save body's `ruleScope` selects the document (absent = the landlord's all-properties rules).
 * Errors propagate as `apiFetch`'s `ApiClientError` (carries `status`); a 422 from the save
 * route is thrown as `PlaybookValidationError`, carrying the per-path errors.
 */

import { apiFetch, ApiClientError } from '../lib/api.core';
import { listProperties } from '../lib/portfolio.api';
import {
  playbookValidationErrorResponseSchema,
  type AgentPlaybookKindView,
  type AgentPlaybooksResponse,
  type AgentPolicyResponse,
  type PatchAgentPolicyBody,
  type PlaybookAgentKind,
  type PolicyAgentKind,
  type PlaybookValidationErrorResponse,
  type SaveAgentPlaybookBody,
} from '@resida/api-types/agent';

export async function getAgentPlaybooks(): Promise<AgentPlaybooksResponse> {
  return apiFetch<AgentPlaybooksResponse>('/landlord/api/agent-playbooks');
}

/** React Query key for the landlord's active properties as the playbook and conversation panel use them. */
export const PLAYBOOK_PROPERTIES_QUERY_KEY = ['agent-playbook-properties'] as const;

/**
 * The landlord's active properties (BR-PB-SCOPE: only an active property can hold rules), by name.
 * Feeds the "Add property rules" picker and the property name on a conversation's scope chip.
 */
export async function listPlaybookProperties(): Promise<Array<{ id: string; name: string }>> {
  const { properties } = await listProperties();
  return properties
    .filter((p) => p.status === 'active')
    .map((p) => ({ id: p.id, name: p.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Thrown by `saveAgentPlaybook` on a 422: every violation found, as `{ path, message }`. */
export class PlaybookValidationError extends ApiClientError {
  constructor(public readonly body: PlaybookValidationErrorResponse) {
    super('Playbook validation failed', 'PLAYBOOK_VALIDATION', 422, body);
    this.name = 'PlaybookValidationError';
  }
}

/**
 * Save a new playbook version. `apiFetch` drops the body of a 422 that has no `validationResult`,
 * and this route's 422 body (`{ errors: [{ path, message }] }`) is what the editor renders, so the
 * request is made directly. A 401 is handed to `apiFetch` (nothing was saved) so the shared
 * silent-refresh / session-expiry handling still applies.
 */
export async function saveAgentPlaybook(
  kind: PlaybookAgentKind,
  body: SaveAgentPlaybookBody,
): Promise<AgentPlaybookKindView> {
  const endpoint = `/landlord/api/agent-playbooks/${kind}`;
  const init: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
  const response = await fetch(endpoint, init);
  if (response.status === 401) {
    return apiFetch<AgentPlaybookKindView>(endpoint, { method: 'POST', body: init.body });
  }
  const data: unknown = await response.json().catch(() => null);
  if (response.ok) return data as AgentPlaybookKindView;
  if (response.status === 422) {
    const parsed = playbookValidationErrorResponseSchema.safeParse(data);
    if (parsed.success) throw new PlaybookValidationError(parsed.data);
  }
  const message = (data as { message?: string } | null)?.message;
  throw new ApiClientError(
    message ?? 'An unexpected error occurred',
    response.status === 503 ? 'SERVICE_UNAVAILABLE' : 'UNKNOWN_ERROR',
    response.status,
  );
}

export async function getAgentDefaultPolicy(kind: PolicyAgentKind): Promise<AgentPolicyResponse> {
  return apiFetch<AgentPolicyResponse>(`/landlord/api/agent-policy/default/${kind}`);
}

export async function patchAgentDefaultPolicy(
  kind: PolicyAgentKind,
  body: PatchAgentPolicyBody,
): Promise<AgentPolicyResponse> {
  return apiFetch<AgentPolicyResponse>(`/landlord/api/agent-policy/default/${kind}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}
